<script setup lang="ts">
import { safeParse as vSafeParse } from 'valibot';
import { type IadAppRoute, ErrorSchema } from '~/application';

definePageMeta({
  layout: 'sticky',
  pageTemplate: 'collaborator_page',
  pageType: 'collaborator',
});

const route = useRoute();

// “error” has to be renamed, it was crashing during tests
// https://tanstack.com/query/latest/docs/framework/vue/examples/nuxt3
const { data: agent, suspense, error: agentError } = useFetchAgent(route.params.userName);

// https://tanstack.com/query/latest/docs/framework/vue/guides/suspense
await suspense();
const { locale } = useI18n();
const localePath = useLocalePath();

const errorResult = vSafeParse(ErrorSchema, agentError.value);
const searchAgentRoute: IadAppRoute<'find-real-estate-agent'> = {
  name: `find-real-estate-agent`,
  params: {},
};
if (errorResult.success && errorResult.output.statusMessage === `DB_AGENT_USERNAME_NOT_FOUND`) {
  const path = localePath(searchAgentRoute, locale.value);
  if (path) await navigateTo({ path }, { replace: true, redirectCode: 301 });
}
else if (agent.value && agent.value.isDeactivated) {
  const redirectPath = (agent.value.breadcrumbs.at(-2)?.to as IadAppRoute<'find-real-estate-agent-agentSector'>) ?? searchAgentRoute;
  const path = localePath(redirectPath, locale.value);
  if (path) await navigateTo({ path }, { replace: true, redirectCode: 301 });
}

const displayContactForm = ref(false);
watch(displayContactForm, function trackContact(isDialogOpen) {
  if (!isDialogOpen) return;
  trackEvent({
    event: 'agent_interaction',
    event_name: 'click_to_email',
    agent_id: agent.value?.localId,
  });
});

onMounted(() => {
  navHighlighter();
  useResizeObserver(window.document.body, navHighlighter);
});

const { y } = useWindowScroll();
watch(y, navHighlighter);
const { t } = useI18n();

const title = computed(() => {
  if (!agent.value) return ``;
  return t(`agent.meta.title_${agent.value.displayGender}`, {
    fullName: agent.value.fullName,
    place: agent.value.location?.place,
    postcode: agent.value.location.postcode,
  });
});

const description = computed(() => {
  if (!agent.value) return ``;
  return t(`agent.meta.description_${agent.value.displayGender}`, {
    fullName: agent.value.fullName,
    place: agent.value.location?.place,
    postcode: agent.value.location.postcode,
  });
});

const openGraphImage = useOpenGraphImageResize({
  src: toRef(() => agent.value?.avatar.src),
});

useHead({
  title: title.value,
  htmlAttrs: {
    class: `scroll-smooth`,
  },
  meta: [
    { property: `og:title`, content: title.value },
    { name: `description`, content: description.value },
    { property: `og:description`, content: description.value },
    { property: `og:image`, content: openGraphImage.value },
    { name: `twitter:title`, content: title.value },
    { name: `twitter:description`, content: description.value },
    { name: `twitter:image`, content: openGraphImage.value },
  ],
});

const section = ref(`properties`);
// https://gist.github.com/khuongyolo/4e7d8308c01733961f7ad0c9964db591
function navHighlighter() {
  const scrollY = Math.round(y.value);
  // Now we loop through sections to get height, top and ID values for each
  document.querySelectorAll(`.js-navigation-item`).forEach((current) => {
    if (!(current instanceof HTMLElement)) return;
    const sectionHeight = current.offsetHeight;

    // Original:
    // const sectionTop = current.offsetTop - 50;
    //
    // Alex Turnwall's update:
    // Updated original line (above) to use getBoundingClientRect instead of offsetTop, based on:
    // https://plainjs.com/javascript/styles/get-the-position-of-an-element-relative-to-the-document-24/
    // https://newbedev.com/difference-between-getboundingclientrect-top-and-offsettop
    // This allows the use of sections inside a relative parent, which I'm not using here, but needed for a project
    const headerHeight = Math.round(document.querySelector<HTMLElement>(`.js-agent-navigation`)?.offsetHeight || 80);
    const sectionTop = Math.round((current.getBoundingClientRect().top + window.scrollY) - headerHeight);
    const sectionId = current.getAttribute('id');
    if (!sectionId) return;

    /*
    - If our current scroll position enters the space where current section on screen is, that's our section
    - To know which link needs an active class, we use sectionId variable we are getting while looping through sections as an selector
    */
    if (scrollY >= sectionTop && scrollY <= sectionTop + sectionHeight) {
      section.value = sectionId;
    }
  });
}

const displayError = computed(() => {
  return agentError.value instanceof Error && 'statusCode' in agentError.value && agentError.value.statusCode === 404;
});
</script>

<template>
  <div class="contents">
    <IadPlaceholderNotFound v-if="displayError" />
    <main v-if="agent">
      <AgentSectionHeader :agent />
      <AgentNavigation
        v-model="displayContactForm"
        class="js-agent-navigation"
        :section
        :agent
      />
      <div
        id="properties"
        class="flex flex-col gap-l py-l js-navigation-item scroll-mt-header lg:scroll-mt-20"
      >
        <AgentSectionProperties
          v-if="agent.propertyCount"
          :agent
          title-tag="p"
        />
        <AgentSectionSoldProperties
          v-if="agent.hasLatestTransactions"
          :latest-transactions="agent.latestTransactions"
          title-tag="p"
        />
        <AgentSectionPropertips :agent />
      </div>
      <AgentSectionAbout
        id="about"
        class="js-navigation-item scroll-mt-header lg:scroll-mt-20"
        :agent
      />
      <AgentSectionReviews
        v-if="agent.reviewsCount"
        id="reviews"
        class="js-navigation-item scroll-mt-header lg:scroll-mt-20"
        :agent
        title-tag="h3"
      />
      <AgentSectionCommitments
        title-tag="h3"
        title-accordion-tag="p"
      />
      <IadWrapper
        class="py-s"
        tag="div"
      >
        <IadBreadcrumbs
          :items="agent.breadcrumbs"
          class="py-2xl"
        />
        <p
          class="whitespace-pre-line text-neutral-40"
          v-text="$t('agent.legal_notice')"
        />
      </IadWrapper>
      <ClientOnly>
        <AgentNavigationFixed
          :agent
          @contact="displayContactForm = true"
        />
        <AgentDialogContact
          v-model="displayContactForm"
          :agent
        />
      </ClientOnly>
    </main>
  </div>
</template>
